import { useSyncExternalStore } from "react";
import { CORES_DESTAQUE, MODOS_APARENCIA, TONS_FUNDO, type Eu, type Preferencias } from "@horofacial/shared";
import { api } from "./api";

/**
 * Aparência de cada usuário: modo (sistema, claro, escuro), cor de destaque e tom do fundo.
 * Fica no servidor (vale em qualquer aparelho); a cópia no navegador só evita piscar o tema errado ao abrir.
 * Cor null = a da clínica, se ela tiver uma; senão, o verde padrão.
 */
export type Aparencia = Preferencias;
type MarcaCores = { corClaro: string | null; corEscuro: string | null };

const CHAVE = "hf_aparencia";
const PADRAO: Aparencia = { modo: "sistema", cor: null, tom: "marfim" };

function valida(a: unknown): Aparencia {
  const x = (a ?? {}) as Partial<Record<keyof Aparencia, unknown>>;
  return {
    modo: MODOS_APARENCIA.includes(x.modo as never) ? (x.modo as Aparencia["modo"]) : PADRAO.modo,
    cor: CORES_DESTAQUE.includes(x.cor as never) ? (x.cor as Aparencia["cor"]) : null,
    tom: TONS_FUNDO.includes(x.tom as never) ? (x.tom as Aparencia["tom"]) : PADRAO.tom,
  };
}

function lerLocal(): { aparencia: Aparencia; marca: MarcaCores } {
  try {
    const j = JSON.parse(localStorage.getItem(CHAVE) ?? "null") as { aparencia?: unknown; marca?: MarcaCores } | null;
    return { aparencia: valida(j?.aparencia), marca: { corClaro: j?.marca?.corClaro ?? null, corEscuro: j?.marca?.corEscuro ?? null } };
  } catch {
    return { aparencia: PADRAO, marca: { corClaro: null, corEscuro: null } };
  }
}

function gravarLocal() {
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ aparencia: atual, marca }));
  } catch {
    // Sem armazenamento (aba privada): vale o que veio do servidor.
  }
}

const escuroNoSistema = () => matchMedia("(prefers-color-scheme: dark)").matches;

/** A clínica tem cor própria? Sem ela, a opção "Cor da clínica" não aparece. */
export const clinicaTemCor = () => !!marca.corClaro && !!marca.corEscuro;

/** Grava os data-* em <html> que o CSS lê (ver estilos.css). */
function aplicar(a: Aparencia) {
  const raiz = document.documentElement;
  if (a.modo === "sistema") raiz.removeAttribute("data-tema");
  else raiz.setAttribute("data-tema", a.modo);
  const cor = a.cor ?? (clinicaTemCor() ? "clinica" : "verde");
  if (cor === "verde" || (cor === "clinica" && !clinicaTemCor())) raiz.removeAttribute("data-cor");
  else raiz.setAttribute("data-cor", cor);
  if (a.tom === "marfim") raiz.removeAttribute("data-tom");
  else raiz.setAttribute("data-tom", a.tom);
  if (clinicaTemCor()) {
    raiz.style.setProperty("--marca-clinica-claro", marca.corClaro);
    raiz.style.setProperty("--marca-clinica-escuro", marca.corEscuro);
  } else {
    raiz.style.removeProperty("--marca-clinica-claro");
    raiz.style.removeProperty("--marca-clinica-escuro");
  }
  // A barra do navegador no celular acompanha o fundo em uso.
  const escuro = a.modo === "escuro" || (a.modo === "sistema" && escuroNoSistema());
  const fundo = document.body ? getComputedStyle(document.body).backgroundColor : "";
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
    m.content = fundo || (escuro ? "#0f0f10" : "#f9f8f6");
    m.removeAttribute("media");
  });
}

const local = lerLocal();
// Um valor só para a aplicação inteira (o painel aparece no menu lateral, na folha "Mais" e em Minha conta).
let atual: Aparencia = local.aparencia;
let marca: MarcaCores = local.marca;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((o) => o());

// Antes do login (entrada, recuperação e etapas) a tela é a mesma para todos: escuro, sem cor de clínica.
// A marca no navegador evita que quem já está logado veja o escuro piscar ao abrir.
const CHAVE_ENTROU = "hf_entrou";
let antesDoLogin = (() => {
  try {
    return localStorage.getItem(CHAVE_ENTROU) !== "1";
  } catch {
    return true;
  }
})();

const ENTRADA: Aparencia = { modo: "escuro", cor: "verde", tom: "marfim" };

/** Aplica a aparência em uso agora: a da entrada antes do login, a do usuário depois. */
export function aplicarAparencia() {
  aplicar(antesDoLogin ? ENTRADA : atual);
}

/** A sessão entrou ou saiu: troca entre a aparência da entrada e a do usuário. */
export function marcarSessao(dentro: boolean) {
  antesDoLogin = !dentro;
  try {
    if (dentro) localStorage.setItem(CHAVE_ENTROU, "1");
    else localStorage.removeItem(CHAVE_ENTROU);
  } catch {
    // Sem armazenamento (aba privada): vale só nesta visita.
  }
  aplicarAparencia();
}

/** Chegou o "eu" do servidor: ele manda (a escolha pode ter mudado em outro aparelho, ou a cor da clínica). */
export function sincronizarAparencia(eu: Eu) {
  atual = valida(eu.usuario.preferencias);
  marca = { corClaro: eu.empresa.corClaro, corEscuro: eu.empresa.corEscuro };
  gravarLocal();
  aplicarAparencia();
  avisar();
}

/** Troca na tela na hora e grava no servidor; se a gravação falhar, a escolha vale só neste navegador. */
export function definirAparencia(mudanca: Partial<Aparencia>) {
  atual = { ...atual, ...mudanca };
  gravarLocal();
  aplicarAparencia();
  avisar();
  return api<void>("/auth/preferencias", { method: "PUT", json: atual });
}

// Seguindo o sistema, acompanha a troca de claro/escuro do aparelho (só a cor da barra muda; o CSS já acompanha).
matchMedia("(prefers-color-scheme: dark)").addEventListener("change", aplicarAparencia);

export function useAparencia() {
  return useSyncExternalStore(
    (o) => (ouvintes.add(o), () => ouvintes.delete(o)),
    () => atual,
  );
}
